import { useQueryClient } from "@tanstack/react-query";
import { useSetAtom } from "jotai";
import { SyntheticEvent } from "react";
import { useNavigate } from "react-router-dom";

import { useQueryParam } from "@/hooks/useQueryParam";
import { clearContext } from "@/provider/DataContextProvider";
import { useLogout as useApiLogout } from "@/services/auth";
import {
  addDeviceFormDataAtom,
  AlertTypes,
  clearAuthAtom,
  showsSessionExpiredModalAtom,
  verifierAtom,
} from "@/utils/atoms";

import { useAlert } from "./useAlert";

export const useLogout = () => {
  const clearAuth = useSetAtom(clearAuthAtom);
  const setShowSessionExpiredModal = useSetAtom(showsSessionExpiredModalAtom);
  const setAddDeviceFormData = useSetAtom(addDeviceFormDataAtom);
  const showAlert = useAlert();

  const setVerifier = useSetAtom(verifierAtom);
  const [, setCode] = useQueryParam("code");
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const clearAppContext = () => {
    const isPartnerRoute =
      window.location.hash.startsWith("#/partner") ||
      window.location.hash.startsWith("#/welcome-partner");
    clearContext();
    setCode("");
    queryClient.clear();
    clearAuth();
    setVerifier(undefined);
    setShowSessionExpiredModal(undefined);
    setAddDeviceFormData(null);
    localStorage.clear();
    // Remove info from sessionStorage to show attention bubble again after login
    sessionStorage.removeItem("showFriendsWithBenefitsHint");
    navigate(isPartnerRoute ? "/welcome-partner" : "/welcome");
  };

  const { mutate } = useApiLogout({
    mutation: {
      onSuccess: () => {
        clearAppContext();
      },
      onError: () => {
        clearAppContext();
      },
    },
  });

  // As this is commonly wired straight into button onClicks, React might call it with the SyntheticEvent...
  const logout = (emailVerificationErrorOrEvent?: string | SyntheticEvent) => {
    // ...so we need to avoid an event triggering an error alert
    const emailVerificationError =
      typeof emailVerificationErrorOrEvent === "string"
        ? emailVerificationErrorOrEvent
        : undefined;

    if (emailVerificationError) {
      showAlert({
        type: AlertTypes.error,
        text: emailVerificationError,
      });
    }

    mutate();
  };

  return logout;
};
